import { PreviewCanvasFrame, renderPreview } from '@/components/fpsr-viewer';
import {
    DEFAULT_FPSR_BACKGROUND,
    fpsrBackgroundKey,
    parseFpsrPathFromMeta,
} from '@/lib/fpsr';
import type { BlueprintDocument, RenderBackground } from '@rickyzhangca/fpsr';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type FpsrFitPreviewProps = {
  blueprintString: string;
  meta: string[] | null | undefined;
  imageUrl: string | null | undefined;
  title: string;
  background?: RenderBackground;
  onPreviewWidth?: (width: number | null) => void;
};

const FULL_PIXELS_PER_TILE = 64;
const MAX_OUTPUT_SIZE = { width: 4096, height: 4096 };
const MAX_PREVIEW_EDGE = 8192;
const MAX_PREVIEW_PIXELS = 32_000_000;
const PREVIEW_OVERSCAN = 2;

const adaptivePreviewSize = (viewport: { width: number; height: number }) => {
  const density = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
  let width = Math.min(
    MAX_PREVIEW_EDGE,
    Math.max(1024, viewport.width * density * PREVIEW_OVERSCAN)
  );
  let height = Math.min(
    MAX_PREVIEW_EDGE,
    Math.max(1024, viewport.height * density * PREVIEW_OVERSCAN)
  );
  const pixels = width * height;
  if (pixels > MAX_PREVIEW_PIXELS) {
    const scale = Math.sqrt(MAX_PREVIEW_PIXELS / pixels);
    width *= scale;
    height *= scale;
  }
  return { width: Math.floor(width), height: Math.floor(height) };
};

export const FpsrFitPreview = ({
  blueprintString,
  meta,
  imageUrl,
  title,
  background = DEFAULT_FPSR_BACKGROUND,
  onPreviewWidth,
}: FpsrFitPreviewProps) => {
  const [canvasEl, setCanvasEl] = useState<HTMLCanvasElement | null>(null);
  const renderGenRef = useRef(0);
  const maxOutputRef = useRef(MAX_OUTPUT_SIZE);
  const [doc, setDoc] = useState<BlueprintDocument | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [dimensions, setDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [maxOutputSize, setMaxOutputSize] = useState<{
    width: number;
    height: number;
  }>(MAX_OUTPUT_SIZE);

  maxOutputRef.current = maxOutputSize;
  const backgroundRef = useRef(background);
  backgroundRef.current = background;
  const backgroundKey = fpsrBackgroundKey(background);

  const blueprintPathKey = useMemo(() => {
    const path = parseFpsrPathFromMeta(meta);
    return path?.join(',') ?? '';
  }, [meta]);

  const blueprintPath = useMemo(() => {
    if (!blueprintPathKey) return undefined;
    return blueprintPathKey.split(',').map(Number);
  }, [blueprintPathKey]);

  const setCanvasNode = useCallback((node: HTMLCanvasElement | null) => {
    setCanvasEl(node);
  }, []);

  const handleViewportSizeChange = useCallback(
    (size: { width: number; height: number }) => {
      onPreviewWidth?.(Math.round(size.width));
      const next = adaptivePreviewSize(size);
      setMaxOutputSize(current =>
        current.width === next.width && current.height === next.height
          ? current
          : next
      );
    },
    [onPreviewWidth]
  );

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    setDimensions(null);
    setDoc(null);

    void (async () => {
      try {
        const { decode } = await import('@rickyzhangca/fpsr');
        const decoded = decode(blueprintString);
        if (cancelled) return;
        setDoc(decoded);
      } catch (err) {
        if (cancelled) return;
        console.error('FPSR decode failed:', err);
        setStatus('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [blueprintString]);

  useEffect(() => {
    if (!doc || !canvasEl) return;

    const gen = ++renderGenRef.current;
    const controller = new AbortController();
    setStatus(prev => (prev === 'ready' ? prev : 'loading'));

    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const result = await renderPreview(canvasEl, doc, {
            blueprintPath,
            pixelsPerTile: FULL_PIXELS_PER_TILE,
            maxOutputSize: maxOutputRef.current,
            background: backgroundRef.current,
            signal: controller.signal,
          });
          if (gen !== renderGenRef.current || controller.signal.aborted) return;
          setDimensions({ width: result.width, height: result.height });
          setStatus('ready');
        } catch (err) {
          if (controller.signal.aborted) return;
          if (gen !== renderGenRef.current) return;
          console.error('FPSR dialog render failed:', err);
          setDimensions(null);
          setStatus('error');
        }
      })();
    }, 150);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [doc, canvasEl, blueprintPath, maxOutputSize, backgroundKey]);

  useEffect(() => {
    return () => {
      onPreviewWidth?.(null);
    };
  }, [onPreviewWidth]);

  const showFallback =
    (status === 'loading' || status === 'error') && Boolean(imageUrl);

  return (
    <div className="absolute inset-0">
      {showFallback && (
        <img
          src={imageUrl!}
          alt={`Blueprint preview: ${title}`}
          className="absolute inset-0 m-auto max-h-full max-w-full object-contain"
          loading="eager"
        />
      )}
      <PreviewCanvasFrame
        className="absolute inset-0"
        width={dimensions?.width}
        height={dimensions?.height}
        onViewportSizeChange={handleViewportSizeChange}
      >
        <canvas
          ref={setCanvasNode}
          className="size-full [image-rendering:pixelated]"
          role="img"
          aria-label={`Blueprint preview: ${title}`}
        />
      </PreviewCanvasFrame>
    </div>
  );
};
